import { ComponentBase } from "../../core/ComponentBase.js";

const FILE_ICONS = {
    // code
    py: 'bi-filetype-py', js: 'bi-filetype-js', ts: 'bi-filetype-tsx',
    sh: 'bi-terminal', bash: 'bi-terminal', json: 'bi-filetype-json',
    yaml: 'bi-file-code', yml: 'bi-file-code', toml: 'bi-file-code',
    env: 'bi-file-lock', sql: 'bi-file-code', html: 'bi-filetype-html',
    css: 'bi-filetype-css', xml: 'bi-file-code', md: 'bi-file-richtext',
    // data
    csv: 'bi-filetype-csv', xlsx: 'bi-filetype-xlsx', xls: 'bi-filetype-xls',
    // text
    txt: 'bi-file-text', log: 'bi-journal-text',
    // images
    png: 'bi-file-image', jpg: 'bi-file-image', jpeg: 'bi-file-image',
    gif: 'bi-file-image', svg: 'bi-file-image', webp: 'bi-file-image',
    // media
    mp4: 'bi-file-play', webm: 'bi-file-play', mp3: 'bi-file-music',
    wav: 'bi-file-music',
    // archives
    zip: 'bi-file-zip', gz: 'bi-file-zip', tar: 'bi-file-zip',
    // docs
    pdf: 'bi-file-pdf',
};

function fileIcon(name) {
    const ext = (name.split('.').pop() || '').toLowerCase();
    return FILE_ICONS[ext] || 'bi-file-earmark';
}

function formatSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export class AgentFilesPanel extends ComponentBase {
    static styles = `
        .afp-drawer {
            position: absolute; top: 0; right: 0; bottom: 0;
            width: 320px; background: var(--bs-body-bg);
            border-left: 1px solid var(--bs-border-color);
            display: flex; flex-direction: column;
            transform: translateX(100%); transition: transform var(--ab-t-slow), visibility var(--ab-t-slow);
            z-index: var(--ab-z-sidebar); box-shadow: var(--ab-shadow-lg);
            visibility: hidden;
        }
        .afp-drawer.open { transform: translateX(0); visibility: visible; }
        .afp-header {
            padding: 10px 12px; border-bottom: 1px solid var(--bs-border-color-translucent);
            display: flex; align-items: center; gap: 8px; flex-shrink: 0;
        }
        .afp-title { font-weight: var(--ab-weight-semibold); font-size: var(--ab-text-sm); flex: 1; }
        .afp-space { max-width: 132px; font-size: var(--ab-text-xs); }
        .afp-breadcrumb {
            padding: 6px 12px; font-size: var(--ab-text-xs);
            border-bottom: 1px solid var(--bs-border-color-translucent);
            display: flex; align-items: center; gap: 4px; flex-wrap: wrap;
            flex-shrink: 0; color: var(--bs-secondary-color);
            min-height: 32px;
        }
        .afp-crumb { cursor: pointer; color: var(--bs-link-color); transition: color var(--ab-t-fast); }
        @media (hover: hover) {
            .afp-crumb:hover { text-decoration: underline; color: var(--bs-link-hover-color); }
        }
        .afp-crumb-sep { opacity: 0.4; }
        .afp-body { flex: 1; overflow-y: auto; padding: 4px 0; }
        .afp-item {
            display: flex; align-items: center; gap: 10px;
            padding: 7px 12px; font-size: var(--ab-text-sm); cursor: default;
            border-bottom: 1px solid var(--bs-border-color-translucent);
            transition: background-color var(--ab-t-fast);
            outline: none;
        }
        @media (hover: hover) {
            .afp-item:hover { background: var(--ab-hover-bg); }
        }
        .afp-item:focus-visible {
            box-shadow: var(--ab-focus-ring) inset;
        }
        .afp-item-icon { font-size: var(--ab-text-md); flex-shrink: 0; color: var(--bs-secondary-color); }
        .afp-item-icon.folder { color: var(--bs-warning); }
        .afp-item-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .afp-item-size { font-size: var(--ab-text-2xs); color: var(--bs-secondary-color); flex-shrink: 0; }
        .afp-item-actions { display: flex; gap: 4px; flex-shrink: 0; }
        @media (max-width: 768px) {
            .afp-drawer { width: 100%; border-left: none; }
        }
    `;

    constructor(config) {
        super(config);
        this.workspace  = config.workspace;
        this.threadId   = null;
        this.prefix     = '';
        this.files      = [];
        this.spaces     = [];
        this.spaceId    = null;
        this.loading    = false;
        this.isOpen     = false;
        this.drawer     = null;
        this.applyStyles();
    }

    t(text, replacements) {
        return this.appClient.tx.t(text, replacements);
    }

    async render(parentEl) {
        this.drawer = document.createElement('div');
        this.drawer.className = 'afp-drawer';
        this.drawer.innerHTML = `
            <div class="afp-header">
                <i class="bi bi-folder2-open text-warning" aria-hidden="true"></i>
                <span class="afp-title">${this.t('Agent Files')}</span>
                <select class="form-select form-select-sm afp-space" id="afp-space" title="${this.t('Workspace space')}"></select>
                <button class="ab-icon-btn ab-icon-btn-sm text-body" id="afp-refresh" title="${this.t('Refresh')}" aria-label="${this.t('Refresh')}">
                    <i class="bi bi-arrow-clockwise"></i>
                </button>
                <button class="ab-icon-btn ab-icon-btn-sm text-body" id="afp-close" title="${this.t('Close')}" aria-label="${this.t('Close')}">
                    <i class="bi bi-x-lg"></i>
                </button>
            </div>
            <div class="afp-breadcrumb" id="afp-breadcrumb"></div>
            <div class="afp-body" id="afp-body"></div>
        `;
        parentEl.appendChild(this.drawer);

        this.drawer.querySelector('#afp-close').addEventListener('click', () => this.close());
        this.drawer.querySelector('#afp-refresh').addEventListener('click', () => this.refresh());
        this.drawer.querySelector('#afp-space').addEventListener('change', event => {
            this.spaceId = event.target.value;
            this.prefix = '';
            this.loadFiles();
        });
    }

    open(threadId) {
        this.threadId = threadId;
        this.prefix   = '';
        this.spaceId  = `thread:${threadId}`;
        this.isOpen   = true;
        this.drawer.classList.add('open');
        this.notifyWorkspace(true);
        // Show spinner immediately so the drawer doesn't appear blank
        this.loading = true;
        this._renderBody();
        this.loadSpaces().then(() => this.loadFiles());
    }

    close() {
        this.isOpen = false;
        this.drawer.classList.remove('open');
        this.notifyWorkspace(false);
    }

    refresh() {
        const btn = this.drawer.querySelector('#afp-refresh i');
        if (btn) btn.classList.add('spin');
        this.loadFiles().finally(() => {
            if (btn) btn.classList.remove('spin');
        });
    }

    notifyWorkspace(open) {
        this.appClient.events.fireLocal('agentFilesPanelToggled', { open });
    }

    async loadSpaces() {
        try {
            this.spaces = await this.appClient.rpcClient.call(
                'ThreadRpc.listWorkspaceSpaces',
                { threadId: this.threadId, agentName: this.workspace.currentAgentName },
                { projectId: this.workspace.currentProjectId }
            ) || [];
        } catch (err) {
            this.log.error('AgentFilesPanel: loadSpaces failed', err);
            this.spaces = [{ spaceId: `thread:${this.threadId}`, label: this.t('Current Conversation') }];
        }
        if (!this.spaces.some(space => space.spaceId === this.spaceId)) {
            this.spaceId = this.spaces[0]?.spaceId || `thread:${this.threadId}`;
        }
        const select = this.drawer.querySelector('#afp-space');
        select.innerHTML = this.spaces.map(space =>
            `<option value="${this.escapeHtml(space.spaceId)}">${this.escapeHtml(space.label || space.spaceId)}</option>`
        ).join('');
        select.value = this.spaceId;
    }

    async loadFiles() {
        if (!this.threadId) return;
        this.loading = true;
        this._renderBody();

        try {
            const result = await this.appClient.rpcClient.call(
                'ThreadRpc.listAgentFiles',
                { threadId: this.threadId, agentName: this.workspace.currentAgentName, spaceId: this.spaceId, prefix: this.prefix },
                { projectId: this.workspace.currentProjectId }
            );
            this.files = result || [];
        } catch (err) {
            this.log.error('AgentFilesPanel: loadFiles failed', err);
            this.files = [];
        }

        this.loading = false;
        this._renderBreadcrumb();
        this._renderBody();
    }

    _renderBreadcrumb() {
        const el = this.drawer.querySelector('#afp-breadcrumb');
        const parts = this.prefix ? this.prefix.replace(/\/$/, '').split('/') : [];

        const label = this.spaces.find(space => space.spaceId === this.spaceId)?.label || this.spaceId;
        let html = `<span class="afp-crumb" data-prefix="" tabindex="0" role="link">${this.escapeHtml(label || this.t('Workspace'))}</span>`;
        let cumulative = '';
        for (const part of parts) {
            cumulative += part + '/';
            const p = cumulative;
            html += `<span class="afp-crumb-sep">›</span>
                     <span class="afp-crumb" data-prefix="${this.escapeHtml(p)}" tabindex="0" role="link">${this.escapeHtml(part)}</span>`;
        }
        el.innerHTML = html;
        el.querySelectorAll('.afp-crumb').forEach(crumb => {
            const triggerCrumb = () => {
                this.prefix = crumb.dataset.prefix;
                this.loadFiles();
            };
            crumb.addEventListener('click', triggerCrumb);
            crumb.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    triggerCrumb();
                }
            });
        });
    }

    _renderBody() {
        const body = this.drawer.querySelector('#afp-body');

        if (this.loading) {
            body.innerHTML = `
                <div class="ab-loading">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                        <span class="visually-hidden">${this.t('Loading...')}</span>
                    </div>
                    <span>${this.t('Loading...')}</span>
                </div>`;
            return;
        }

        // Use isDir flag from filer API — no need to infer folders from path slashes
        // Filter out hidden/system folders like .config, .cache, .local, .vnc
        const hiddenSystemFolders = ['.config', '.cache', '.local', '.vnc', '.ICEauthority', '.Xauthority'];
        const visibleFiles = this.files.filter(f => {
            const rootItemName = f.path.split('/')[0];
            return !hiddenSystemFolders.includes(rootItemName);
        });

        const folders   = visibleFiles.filter(f => f.isDir);
        const fileItems = visibleFiles.filter(f => !f.isDir);

        if (!folders.length && !fileItems.length) {
            body.innerHTML = `
                <div class="ab-empty">
                    <i class="bi bi-folder2 ab-empty-icon" aria-hidden="true"></i>
                    <div class="ab-empty-title">${this.t('No files yet')}</div>
                    <div class="ab-empty-body">${this.t('Files created in this workspace space will appear here.')}</div>
                </div>`;
            return;
        }

        let html = '';

        // Folders first
        for (const folder of [...folders].sort((a,b) => a.name.localeCompare(b.name))) {
            html += `<div class="afp-item afp-folder-item" data-path="${this.escapeHtml(folder.path)}" tabindex="0" role="option" aria-label="${this.escapeHtml(folder.name)}">
                <i class="bi bi-folder-fill afp-item-icon folder" aria-hidden="true"></i>
                <span class="afp-item-name">${this.escapeHtml(folder.name)}/</span>
                <span class="afp-item-size"></span>
                <div class="afp-item-actions">
                    <button class="ab-icon-btn ab-icon-btn-sm text-secondary" title="${this.t('Open folder')}" aria-label="${this.t('Open folder')}">
                        <i class="bi bi-arrow-right-circle"></i>
                    </button>
                </div>
            </div>`;
        }

        // Files
        for (const file of fileItems.sort((a, b) => a.name.localeCompare(b.name))) {
            const icon = fileIcon(file.name);
            const size = formatSize(file.size);
            html += `<div class="afp-item afp-file-item" data-path="${this.escapeHtml(file.path)}" data-name="${this.escapeHtml(file.name)}" tabindex="0" role="option" aria-label="${this.escapeHtml(file.name)}">
                <i class="bi ${icon} afp-item-icon" aria-hidden="true"></i>
                <span class="afp-item-name" title="${this.escapeHtml(file.name)}">${this.escapeHtml(file.name)}</span>
                <span class="afp-item-size">${size}</span>
                <div class="afp-item-actions">
                    <button class="ab-icon-btn ab-icon-btn-sm text-secondary afp-preview-btn" title="${this.t('Preview')}" aria-label="${this.t('Preview')}" data-path="${this.escapeHtml(file.path)}" data-name="${this.escapeHtml(file.name)}">
                        <i class="bi bi-eye"></i>
                    </button>
                    <button class="ab-icon-btn ab-icon-btn-sm text-secondary afp-download-btn" title="${this.t('Download')}" aria-label="${this.t('Download')}" data-path="${this.escapeHtml(file.path)}">
                        <i class="bi bi-download"></i>
                    </button>
                </div>
            </div>`;
        }

        body.innerHTML = html;

        // Folder click — path is already relative to session root
        body.querySelectorAll('.afp-folder-item').forEach(el => {
            const navigate = () => {
                this.prefix = el.dataset.path;
                this.loadFiles();
            };
            el.addEventListener('click', navigate);
            el.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate();
                }
            });
        });

        // File click/preview or focus keydown
        body.querySelectorAll('.afp-file-item').forEach(el => {
            const preview = () => {
                this.appClient.events.fireLocal('previewAgentFile', {
                    threadId: this.threadId,
                    agentName: this.workspace.currentAgentName,
                    spaceId: this.spaceId,
                    filePath: el.dataset.path,
                    fileName: el.dataset.name,
                    projectId: this.workspace.currentProjectId,
                });
            };
            el.addEventListener('click', (e) => {
                if (e.target.closest('.ab-icon-btn')) return;
                preview();
            });
            el.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    preview();
                }
            });
        });

        // Preview button
        body.querySelectorAll('.afp-preview-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.appClient.events.fireLocal('previewAgentFile', {
                    threadId: this.threadId,
                    agentName: this.workspace.currentAgentName,
                    spaceId: this.spaceId,
                    filePath: btn.dataset.path,
                    fileName: btn.dataset.name,
                    projectId: this.workspace.currentProjectId,
                });
            });
        });

        // Download button
        body.querySelectorAll('.afp-download-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation();
                await this._downloadFile(btn.dataset.path);
            });
        });
    }

    async _downloadFile(filePath) {
        try {
            const res = await this.appClient.rpcClient.call(
                'ThreadRpc.getAgentFileUrl',
                { threadId: this.threadId, agentName: this.workspace.currentAgentName, spaceId: this.spaceId, filePath },
                { projectId: this.workspace.currentProjectId }
            );
            if (res?.url) {
                const a = document.createElement('a');
                a.href = res.url;
                a.download = filePath.split('/').pop();
                a.target = '_blank';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
        } catch (err) {
            this.appClient.notifier?.error(this.t('Could not get download URL'));
            this.log.error('AgentFilesPanel: download failed', err);
        }
    }
}
